import type * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationPageVariablesSideSheet,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import type { HiveWorkingBranch } from '@gitbook/hive-client';
import type { Revision, RevisionPageDocument } from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import {
    Heading,
    LoadingPanel,
    SegmentedControl,
    SidePanel,
    SidePanelBody,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useCurrentHivePage, VariablesForm } from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

/**
 * Side panel to view and edit variables
 */
export function VariablesSidePanel(props: {
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
    readOnly: boolean;
}): React.ReactElement {
    const { analyticsContext, screen, readOnly } = props;

    const { close, setSelectedTab, selectedTab } =
        useSideSheetNavigation<NavigationPageVariablesSideSheet>('variables', 'space');

    const hiveBranch = useHiveBranch();

    // Get revision and page
    const { revision } = useHiveRevision();
    const [page] = useCurrentHivePage(revision, screen.page);

    useSetAnalyticsScreen(
        !page
            ? null
            : {
                  ...analyticsContext,
                  screen: 'space_page_variables',
                  page: page.id,
              },
        [analyticsContext]
    );

    return (
        <SidePanel onClose={close}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Variables</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                <SidePanelHeaderSecondary>
                    <SegmentedControl
                        options={[
                            {
                                value: 'page',
                                label: 'This page',
                            },
                            {
                                value: 'space',
                                label: 'All pages',
                            },
                        ]}
                        size="small"
                        active={selectedTab}
                        onChange={(key) => setSelectedTab(key as typeof selectedTab)}
                    />
                </SidePanelHeaderSecondary>
            </SidePanelHeader>
            <SidePanelBody className="p-6" fallback={<LoadingPanel />}>
                {page && selectedTab === 'page' ? (
                    <PageVariablesForm
                        hiveBranch={hiveBranch}
                        page={page}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}
                {selectedTab === 'space' ? (
                    <SpaceVariablesForm
                        hiveBranch={hiveBranch}
                        revision={revision}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}
            </SidePanelBody>
        </SidePanel>
    );
}

function PageVariablesForm(props: {
    hiveBranch: HiveWorkingBranch | null;
    page: RevisionPageDocument;
    readOnly: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { hiveBranch, page, readOnly, analyticsContext } = props;

    return (
        <VariablesForm
            description="Define variables to store values that you can reuse in this page."
            className="flex-1"
            prefix="page.vars."
            values={page.variables ?? {}}
            onChange={(values) => {
                if (readOnly) {
                    return;
                }

                assertIsDefined(hiveBranch);
                const previous = page.variables ?? {};

                for (const name of Object.keys(values)) {
                    if (!(name in previous)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_add_variable',
                            variableName: name,
                        });
                    } else if (previous[name] !== values[name]) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_edit_variable',
                            variableName: name,
                        });
                    }
                }

                for (const name of Object.keys(previous)) {
                    if (!(name in values)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_remove_variable',
                            variableName: name,
                        });
                    }
                }

                hiveBranch.change((change) => {
                    change.updatePageVariables(page.id, values, { replace: true });
                });
            }}
            readOnly={readOnly}
        />
    );
}

function SpaceVariablesForm(props: {
    hiveBranch: HiveWorkingBranch | null;
    revision: Revision;
    readOnly: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { hiveBranch, revision, readOnly, analyticsContext } = props;

    return (
        <VariablesForm
            description="Define variables to store values that you can reuse in all pages of this space."
            prefix="space.vars."
            values={revision.variables ?? {}}
            onChange={(values) => {
                if (readOnly) {
                    return;
                }

                assertIsDefined(hiveBranch);
                const previous = revision.variables ?? {};

                for (const name of Object.keys(values)) {
                    if (!(name in previous)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_add_variable',
                            variableName: name,
                        });
                    } else if (previous[name] !== values[name]) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_edit_variable',
                            variableName: name,
                        });
                    }
                }

                for (const name of Object.keys(previous)) {
                    if (!(name in values)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_remove_variable',
                            variableName: name,
                        });
                    }
                }

                hiveBranch.change((change) => {
                    change.updateRevisionVariables(values, { replace: true });
                });
            }}
            readOnly={readOnly}
            className="flex-1"
        />
    );
}
